import React, {useState, useEffect} from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useRenameFolderMutation} from '../../../data/documentWorkspaceApi';
import PropTypes from 'prop-types';
import {DARK_TEXT_COLOR, WHITE_100} from '../../../theme';

const styles = {
  modal: {
    base: {
      borderRadius: '0.5rem',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
      backgroundColor: WHITE_100,
      minHeight: '220px',
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '150%',
      letterSpacing: '-0.14px',
    },
    divider: {
      borderBottomWidth: '0.0625rem',
      opacity: 1,
      color: '#DDe0DE',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '140%',
      color: DARK_TEXT_COLOR,
    },
  },
  createButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: '#FFFFFF',
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#229884',
    },
    '&:disabled': {
      backgroundColor: '#81C3B8',
      color: '#FFFFFF',
    },
  },
  cancelButton: {
    borderRadius: '0.5rem',
    // border: '1px solid #DDE0DE',
    background: WHITE_100,
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
};

const RenameFolderModal = ({open, onClose, folder, onSuccess, onError}) => {
  const [folderName, setFolderName] = useState('');
  const [renameFolder] = useRenameFolderMutation();

  useEffect(() => {
    if (folder?.name) {
      setFolderName(folder.name);
    }
  }, [folder]);

  const handleSubmit = async () => {
    let result = null;
    if (folder && folderName && folderName !== folder.name) {
      try {
        result = await renameFolder({
          folderId: folder.id,
          data: {name: folderName, year: folder.year},
        });

        if (result.error) {
          throw new Error(result.error);
        }

        onClose();
        if (onSuccess) {
          onSuccess(`Folder renamed to "${folderName}"`);
        }
      } catch (error) {
        console.error('Error renaming folder:', error);
        if (onError) {
          onError(`Failed: ${result?.error?.data?.error}`);
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width={'500px'} minWidth={'500px'} maxWidth={'500px'}>
          <Stack direction={'column'} sx={styles.modal.base}>
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Typography sx={{...styles.modal.title, fontSize: '16px'}}>
                Rename Folder
              </Typography>
              <IconButton size={'small'} onClick={onClose}>
                <CloseIcon size={'1.25rem'} />
              </IconButton>
            </Stack>

            <Divider orientation={'horizontal'} sx={styles.modal.divider} />

            <Stack direction={'column'} padding={'1.5rem'} spacing={'1.5rem'}>
              <Typography sx={styles.modal.label}>New name *</Typography>
              <TextField
                autoFocus
                fullWidth
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter folder name"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.5rem',
                    height: '2.5rem',
                    '&:hover fieldset': {
                      borderColor: '#26A993',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#26A993',
                    },
                  },
                }}
              />
            </Stack>
            <Divider orientation={'horizontal'} sx={styles.modal.divider} />
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'flex-end'}
              alignItems={'center'}
              spacing={'0.625rem'}
            >
              <Button sx={styles.cancelButton} onClick={onClose}>
                Cancel
              </Button>
              <Button
                sx={styles.createButton}
                onClick={handleSubmit}
                disabled={!folderName || folderName === folder?.name}
              >
                Rename
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default RenameFolderModal;

RenameFolderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    year: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};
